import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Dropdown, Tooltip } from 'antd';
import { ReactComponent as PrivateEye } from '@assets/icons/eye-private.svg';
import { ReactComponent as DeleteSvg } from '@assets/icons/delete.svg';
import { ReactComponent as Download } from '@assets/icons/bulkActions/download.svg';
import { ReactComponent as CopySvg } from '@assets/icons/dropdown/copy.svg';
import { ReactComponent as AWDefaultSvg } from '@assets/icons/AWButtonStatuses/default.svg';
import { ReactComponent as UpgradePlanSvg } from '@assets/icons/diamond-upgrade.svg';
import { ReactComponent as ActionSvg } from '@assets/icons/action.svg';
import { ReactComponent as LinkPlusSvg } from '@assets/icons/dropdown/link-plus.svg';
import { ReactComponent as LinkSvg } from '@assets/icons/dropdown/link.svg';
import { ReactComponent as DuplicateSvg } from '@assets/icons/dropdown/duplicate.svg';
import { ReactComponent as MoveSvg } from '@assets/icons/dropdown/move.svg';
import { AssetItemDto, SimpleFolderItemDto } from '@api/Api';
import { assetGetSimpleFoldersList } from '@api/Asset';
import type { ItemType } from 'antd/lib/menu/hooks/useItems';
import { useCopyAssetsMutation } from '@hooks/queries/assets';
import {
  deleteAssetAction,
  moveAssetToFolder,
  restoreDeletedAssetAction
} from '@redux/actions/assetsAction';
import {
  toggleMoveAssetToPrivateFolder,
  toggleShareAssetModal,
  toggleApprovalModal,
  toggleCopyModal,
  toggleAddToExistingLinkModal
} from '@redux/actions/modalAction';
import { useCurrentWorkspace } from '@hooks/workspace';
import { downloadAll } from '@helpers/download';
import { useOrganization } from '@components/OrganizationBoundary';
import UpgradePlanTooltip from '@components/Tooltip/UpgradePlanTooltip';
import useTypedDispatch from '@hooks/useTypedDispatch';

import './BulkActions.less';

type BulkActionsProps = {
  selectedAssets: { node: AssetItemDto; cursor: string }[];
  canShare: boolean;
  onClose: () => void;
  page: string;
  onRefreshAssetList?: () => void;
};

function BulkActions({
  selectedAssets,
  canShare,
  onClose,
  page,
  onRefreshAssetList
}: BulkActionsProps) {
  const dispatch = useTypedDispatch();
  const navigate = useNavigate();
  const [currentWorkspace] = useCurrentWorkspace(false);
  const canCreateAw = !!currentWorkspace?.permissions.createApprovalWorkflows;
  const { currentOrganization } = useOrganization();
  const [foldersList, setFoldersList] = useState<SimpleFolderItemDto[]>([]);
  const [canBulkMove, setCanBulkMove] = useState<boolean>(false);
  const awFeatureEnabled =
    !!currentOrganization.entity?.features.approvalRouting;

  const { mutateAsync: copyAssets, isPending: isCopyingAssets } =
    useCopyAssetsMutation({});

  const { canBulkDelete, canBulkDownload, canBulkApprovalWorkflow } =
    useMemo(() => {
      if (!selectedAssets.length)
        return {
          canBulkDownload: false,
          canBulkDelete: false,
          canBulkShare: false,
          canBulkApprovalWorkflow: false
        };

      const canBulkDownload: boolean = selectedAssets.every(
        (asset: any) => asset?.node?.asset?.permissions.download
      );
      const canBulkDelete: boolean = selectedAssets.every(
        (asset: any) => asset?.node?.asset?.permissions.delete
      );

      const canBulkApprovalWorkflow = selectedAssets.every(
        (asset: { node: AssetItemDto; cursor: string }) =>
          !asset?.node?.asset?.approvalWorkflow
      );

      return {
        canBulkDownload,
        canBulkDelete,
        canBulkApprovalWorkflow
      };
    }, [selectedAssets]);

  const { length } = selectedAssets;
  const upgradeShare =
    length > 1 && !currentOrganization.entity?.features.shareMultipleAssets;
  const canUseBatchApprovals =
    !!currentOrganization.entity?.features.batchApprovals || length === 1;

  const createApprovalRoutingTooltipText = useMemo(() => {
    if (!awFeatureEnabled) {
      return ' to a paid plan to access this feature.';
    }
    if (!canUseBatchApprovals) {
      return ' to an Enterprise plan to route multiple media assets for approval at one time.';
    }
    if (!canBulkApprovalWorkflow) {
      return length === 1
        ? 'Approval routing is active for the selected file.'
        : 'Approval routing is active for some of the selected files.';
    }
    return '';
  }, [awFeatureEnabled, canUseBatchApprovals, canBulkApprovalWorkflow, length]);

  useEffect(() => {
    if (!selectedAssets.length) return;
    let canMove = selectedAssets
      .map(({ node }: any) => node.campaignId)
      .every((it: any, _idx: any, array: any) => it === array[0]);

    (async () => {
      if (canMove) {
        const {
          data: { list }
        } = await assetGetSimpleFoldersList({
          campaignId: selectedAssets[0].node.campaignId,
          workspaceId: selectedAssets[0].node.workspaceId
        });
        if (list.length === 0) canMove = false;
        setFoldersList(list);
      }

      setCanBulkMove(canMove);
    })();
  }, [selectedAssets]);

  const menuItems: ItemType[] = [
    {
      key: 'duplicate-folder',
      label: 'Duplicate',
      disabled: isCopyingAssets,
      icon: <DuplicateSvg />,
      onClick: () => {
        if (!selectedAssets?.length) return;
        copyAssets({
          workspaceId: selectedAssets[0].node.workspaceId,
          assetIds: selectedAssets.map((asset) => asset.node.id),
          targetCampaignId: selectedAssets[0].node.campaignId,
          targetFolderId: selectedAssets[0].node.path?.[0]?.id,
          copyComments: true
        });
      }
    },
    ...(canBulkMove
      ? [
          {
            key: 'move_to',
            label: 'Move to',
            icon: <MoveSvg />,
            popupClassName: 'asset_links_overlay',
            disabled: !foldersList.length,
            popupOffset: [4, -20],
            children: (foldersList || []).map((folder) => {
              return {
                key: folder.id,
                label: (
                  <>
                    {folder.name.length > 10 ? (
                      <Tooltip
                        placement="topLeft"
                        overlayClassName="toggle_favorite_overlay toggle_favorite_overlay--small"
                        title={folder.name}
                      >
                        <span>{folder.name}</span>
                      </Tooltip>
                    ) : (
                      <span>{folder.name}</span>
                    )}
                    {folder.isPrivate && <PrivateEye />}
                  </>
                ),
                onClick: () => {
                  if (folder.isPrivate) {
                    const assetsName = selectedAssets
                      .map((el) => el.node.asset?.versions[0]?.name || '')
                      .filter(Boolean);
                    dispatch(
                      toggleMoveAssetToPrivateFolder({
                        assetsName,
                        folderName: folder.name,
                        visible: true,
                        callback: () => {
                          for (const asset of selectedAssets) {
                            dispatch(
                              moveAssetToFolder(
                                {
                                  assetId: asset.node.id,
                                  folderId: folder.id
                                },
                                page === 'campaign',
                                asset?.node?.path
                              )
                            );
                          }
                        }
                      })
                    );
                    return;
                  }
                  for (const asset of selectedAssets) {
                    dispatch(
                      moveAssetToFolder(
                        {
                          assetId: asset.node.id,
                          folderId: folder.id
                        },
                        page === 'campaign',
                        asset.node.path
                      )
                    );
                  }
                }
              };
            })
          }
        ]
      : []),
    ...(!canShare || upgradeShare
      ? []
      : [
          {
            type: 'divider' as const
          },
          {
            key: 'add-to-existing-link',
            label: 'Add to existing link',
            icon: <LinkSvg />,
            onClick: () => {
              dispatch(
                toggleAddToExistingLinkModal({
                  visible: true,
                  campaignId: selectedAssets[0].node.campaignId,
                  assets: selectedAssets.map((asset) => ({
                    id: asset.node.id,
                    versionId: asset.node.asset?.versions[0]?.id
                  }))
                })
              );
            }
          }
        ]),

    ...(canBulkDelete
      ? [
          {
            type: 'divider' as const
          },
          {
            key: 'delete',
            label: 'Delete',
            icon: <DeleteSvg className="b-opacity-icon" />,
            onClick: () => {
              if (!canBulkDelete) return;
              for (const asset of selectedAssets) {
                dispatch(
                  deleteAssetAction(
                    { assetId: asset.node.id, permanent: true },
                    asset.node.path[0]?.id
                  )
                );
              }
            }
          }
        ]
      : [])
  ];

  return (
    <div
      className={
        length > 0
          ? 'bulk_actions_container bulk_actions_container_active'
          : 'bulk_actions_container'
      }
    >
      <div className="bulk_actions_container__body">
        <div className="items">
          <div className="list" />
          <div className="copy">{length} media selected</div>
        </div>
        <div className="close" onClick={onClose} />
      </div>
      <div className="bulk_actions_container__actions">
        {page === 'deleted' ? (
          <>
            <Button
              type="text"
              size="small"
              className="action"
              disabled={!canBulkDelete}
              onClick={async () => {
                if (!canBulkDelete) return;
                for (const asset of selectedAssets) {
                  await dispatch(
                    deleteAssetAction(
                      {
                        assetId: asset.node.id,
                        permanent: true
                      },
                      asset.node.path[0]?.id
                    )
                  );
                }
                onClose();
              }}
            >
              <DeleteSvg /> Delete forever
            </Button>
            <Button
              type="text"
              size="small"
              className="action"
              disabled={!canBulkDelete}
              onClick={async () => {
                if (!canBulkDelete) return;
                for (const asset of selectedAssets) {
                  await dispatch(
                    restoreDeletedAssetAction({ assetId: asset.node.id })
                  );
                }
                onClose();
              }}
            >
              Restore
            </Button>
          </>
        ) : (
          <>
            <Dropdown
              trigger={['click']}
              overlayClassName="asset_links_overlay"
              menu={{
                items: menuItems,
                onClick: () => {
                  onClose();
                }
              }}
            >
              <Button type="text" size="small" className="action">
                <ActionSvg className="action__icon" />
                More
              </Button>
            </Dropdown>
            <UpgradePlanTooltip
              text=" to a Team plan to access this feature."
              placement="left"
              isCanVisible={upgradeShare}
            >
              <Button
                type="text"
                size="small"
                className="action"
                disabled={!canShare || upgradeShare}
                onClick={() => {
                  const assets = selectedAssets.map((it: any) => ({
                    id: it.node.id,
                    versionId: it.node.asset?.versions[0]?.id
                  }));
                  dispatch(
                    toggleShareAssetModal({
                      visible: true,
                      assets,
                      campaignId: selectedAssets[0].node.campaignId
                    })
                  );
                  onClose();
                }}
              >
                {upgradeShare && <UpgradePlanSvg className="action__icon" />}
                {!upgradeShare && <LinkPlusSvg className="action__icon" />}
                New share link
              </Button>
            </UpgradePlanTooltip>
            <Button
              type="text"
              size="small"
              className="action"
              onClick={() => {
                if (!selectedAssets?.length) return;
                dispatch(
                  toggleCopyModal({
                    visible: true,
                    type: 'assets',
                    assetIds: selectedAssets.map((asset) => asset.node.id)
                  })
                );
                onClose();
              }}
            >
              <CopySvg className="action__icon action__icon b-copy-icon" />
              Copy to
            </Button>
            <UpgradePlanTooltip
              text={createApprovalRoutingTooltipText}
              placement="top"
              isCanVisible={!!createApprovalRoutingTooltipText}
              isShowUpgradeText={!canUseBatchApprovals || !awFeatureEnabled}
            >
              <Button
                type="text"
                size="small"
                className="action"
                disabled={!!createApprovalRoutingTooltipText}
                onClick={() => {
                  if (
                    !canUseBatchApprovals ||
                    !canBulkApprovalWorkflow ||
                    !awFeatureEnabled
                  )
                    return;
                  const campaignId = selectedAssets?.[0]?.node?.campaignId;
                  if (!campaignId) return;
                  const assetVersions = selectedAssets
                    .filter((item) => !!item.node.asset?.versions[0])
                    .map((item) => ({
                      ...item.node.asset!.versions[0],
                      assetId: item.node.id
                    }));
                  dispatch(
                    toggleApprovalModal({
                      visible: true,
                      assetVersions,
                      campaignId,
                      permissions: {
                        create: canCreateAw
                      },
                      callback: (aw) => {
                        onClose();
                        if (aw) {
                          navigate(
                            `/campaigns/approvals/${aw.workspaceId}/${aw.campaignId}/${aw.id}`
                          );
                          return;
                        }
                        onRefreshAssetList?.();
                      }
                    })
                  );
                }}
              >
                {!canUseBatchApprovals || !awFeatureEnabled ? (
                  <UpgradePlanSvg className="action__icon" />
                ) : (
                  <AWDefaultSvg
                    className="action__icon"
                    style={{ color: 'transparent' }}
                  />
                )}
                Create approval routing
              </Button>
            </UpgradePlanTooltip>

            <Button
              type="text"
              size="small"
              className="action"
              disabled={!canBulkDownload}
              onClick={() => {
                if (!canBulkDownload) return;
                downloadAll(
                  selectedAssets.map(
                    (x: any) => x.node.asset.versions[0].sourceUrl
                  )
                );
                onClose();
              }}
            >
              <Download className="action__icon action__icon" />
              Download
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

export default BulkActions;
